import { useMemo } from "react";
import { basicSort } from "../utils/basicSort";
import { useProductFilters } from "./useProductFilters";
import { FILTER_SELECTION_TYPES } from "../constants/filterSelectionTypes";
import { capitalizeFirstLetter } from "app/utils/utils";

const isValid = value => value !== undefined && value !== null && String(value)?.trim() !== "";

const safeGetter = (getter, product) => {
	try {
		const rawValue = getter(product);

		if (rawValue === undefined || rawValue === null) {
			return [];
		}

		const arrayOfRawValue = Array.isArray(rawValue) ? rawValue : [rawValue];

		return arrayOfRawValue.filter(isValid).map(value => String(value));
	} catch (_error) {
		return [];
	}
};

const getAllOptionValues = (products, getter) => {
	return Array.from(
		new Set(
			products.flatMap(product => {
				return safeGetter(getter, product);
			})
		)
	).sort(basicSort);
};

const countMatchingProductsForOption = ({
	optionValue,
	field,
	filters,
	products,
	facetConfigs,
}) => {
	const simulatedFilters = {
		...filters,
		[field]: [optionValue],
	};

	return products.filter(product => matchesAllFilters(product, simulatedFilters, facetConfigs))
		.length;
};

const matchesAllFilters = (product, activeFilters, facetConfigs) => {
	return facetConfigs.every(({ field, getter }) => {
		const selected = activeFilters[field] || [];
		if (!selected.length) {
			return true;
		}

		const productValues = safeGetter(getter, product);
		const selectedValues = selected.map(v => String(v));
		return selectedValues.some(sel => productValues.includes(sel));
	});
};

export const useFacets = (products = [], facetConfigs) => {
	const { filters } = useProductFilters(facetConfigs.map(config => config.field));

	const filteredProducts = useMemo(() => {
		return products.filter(product => matchesAllFilters(product, filters, facetConfigs));
	}, [products, filters, facetConfigs]);

	const facets = useMemo(() => {
		return Object.fromEntries(
			facetConfigs.map(
				({
					field,
					labelKey,
					getter,
					formatLabel,
					selectionMode = FILTER_SELECTION_TYPES.MULTIPLE,
				}) => {
					const distinctOptions = getAllOptionValues(products, getter);

					const options = distinctOptions.map(optionValue => ({
						value: optionValue,
						label: capitalizeFirstLetter(
							String(formatLabel ? formatLabel(optionValue) : optionValue)
						),
						count: countMatchingProductsForOption({
							optionValue,
							field,
							filters,
							products,
							facetConfigs,
						}),
					}));

					return [field, { labelKey, selectionMode, options }];
				}
			)
		);
	}, [products, filters, facetConfigs]);

	return { filteredProducts, facets };
};
